import { ArrowRight, ArrowDown } from 'lucide-react'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import StripeMotif from '@/components/marketing/StripeMotif'
import type { TechnologyContactAction } from '@/types/technology'

/* The hub's opening statement: one headline, one sentence, ONE action, and an
 * anchor down to the running mockup (plan 2026-10-18). Everything that used to
 * follow it here — the email line, the diagram, the metric strip — either
 * moved to the proof section or left the hub.
 *
 * The atmosphere is built here rather than borrowed from AtmosphericHero: this
 * one needs a taller, quieter field for a headline that is the whole thesis,
 * and a scroll cue, and it is the only hero in the section shaped this way.
 */
export default function TechnologyHero({
  eyebrow,
  title,
  body,
  action,
  anchor,
}: {
  eyebrow: string
  title: string
  body: string
  action: TechnologyContactAction
  anchor: { label: string; href: string }
}) {
  return (
    <section className="marketing-hero-section hero-v2 relative overflow-hidden">
      <div className="hero-spot-warm" aria-hidden="true" />
      <div className="hero-spot-cool" aria-hidden="true" />
      <div className="hero-bottom-fade" aria-hidden="true" />

      <div
        className="pointer-events-none absolute -right-52 -top-24 hidden lg:block"
        style={{ zIndex: 1 }}
        aria-hidden="true"
      >
        <StripeMotif size={420} opacity={0.16} />
      </div>

      <div className="container relative z-10 mx-auto px-6 pb-20 pt-32 lg:pb-24 lg:pt-40">
        <div className="mx-auto max-w-4xl text-center">
          <ScrollReveal>
            <p className="marketing-eyebrow-mono justify-center">{eyebrow}</p>
          </ScrollReveal>

          <ScrollReveal delay={0.07}>
            <h1
              className="hero-headline mt-7"
              style={{ fontSize: 'clamp(2.3rem, 5.6vw, 4.6rem)' }}
            >
              {title}
            </h1>
          </ScrollReveal>

          <ScrollReveal delay={0.13}>
            <p className="marketing-lead mx-auto mt-8 max-w-2xl">{body}</p>
          </ScrollReveal>

          <ScrollReveal delay={0.19}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <a href={action.href} className="marketing-button-primary">
                {action.label}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a href={anchor.href} className="marketing-button-secondary">
                {anchor.label}
                <ArrowDown className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
